<script setup lang="ts">
import type { DynamoPropertyRadius } from '@@/server/property/international-diffusion/dynamo/validator';
import { IAD_PARIS, MAPBOX_PROVIDE_INJECT, useSafePosition, CIRCLE_COLOR } from '~/composables/mapbox/map';
import type { MapBoxInjected, UnsafeLgnLat } from '~/composables/mapbox/map';

const props = withDefaults(
  defineProps<{
    lgnLat?: UnsafeLgnLat
    radius?: DynamoPropertyRadius
  }>(),
  {
    lgnLat: () => IAD_PARIS,
    radius: 1_500,
  },
);

const uuid = useId(); // when multiple circles are rendered on the same map
const { mapbox } = inject(MAPBOX_PROVIDE_INJECT) as MapBoxInjected;

const { position } = useSafePosition(toRef(props, `lgnLat`));

const layerSpecification = shallowRef<mapboxgl.LayerSpecification>();
const source = shallowRef<mapboxgl.SourceSpecification>();

const SOURCE_LAYER_SPECIFICATION_ID = `iad-mapbox-layer-${uuid}`;

// https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js/39006388#39006388
function metersToPixelsAtMaxZoom(meters: number, latitude: number) {
  return meters / 0.075 / Math.cos(latitude * Math.PI / 180);
}

const mapZoom = computed(() => {
  switch (props.radius) {
    case 300:
      return 15;
    case 700:
      return 14;
    case 1_500:
      return 13;
    case 5_000:
      return 11;
    default:
      return 12;
  }
});

watch(mapbox, () => {
  if (!mapbox.value) return;
  mapbox.value?.zoomTo(mapZoom.value);
  // NOTE: Immediate to been trigger during tests
  // Without this
  // • since “provide” will be injected before “mount’
  source.value = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          properties: {
            'property-location': 'property-location',
          },
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: position.value,
          },
        },
      ],
    },
  };

  layerSpecification.value = {
    id: SOURCE_LAYER_SPECIFICATION_ID,
    type: 'circle',
    source: SOURCE_LAYER_SPECIFICATION_ID,
    paint: {
      'circle-radius': [
        'interpolate',
        ['exponential', 2],
        ['zoom'], 0, 0, 20, metersToPixelsAtMaxZoom(props.radius, position.value[1]),
      ],
      'circle-color': CIRCLE_COLOR,
      'circle-opacity': 0.1,
      'circle-stroke-color': CIRCLE_COLOR,
      'circle-stroke-width': 2,
    },
  };

  mapbox.value.on('load', () => {
    if (!mapbox.value || !source.value || !layerSpecification.value) return;
    mapbox.value.addSource(SOURCE_LAYER_SPECIFICATION_ID, source.value);
    mapbox.value.addLayer(layerSpecification.value);
  });
}, { immediate: true });

// Template can't be empty. Needed to avoid Vue warning without rendering anything
// “Avoid app logic that relies on enumerating keys on a component instance. The keys will be empty in production mode to avoid performance overhead.”
const render = () => null;
</script>

<template>
  <render />
</template>
